import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useBannerOffset } from "@hooks/useBannerOffset";

import instance from "@store/middleware/api";

import { bannerSurface } from "@utils/bannerGradient";
import { findAnnouncableCancelledSub } from "@utils/subscriptionHistory";

import { useAuth } from "@/hooks/auth";

/**
 * Site-wide banner shown on every page (except /account*) when the user's
 * most-recent subscription is CANCELLED. Dismissible via × — once dismissed
 * it stays dismissed across logout/login on the same browser. Auto-clears
 * the moment the user re-subscribes (a new ACTIVE sub becomes the
 * most-recent and the banner condition flips off).
 *
 * Dismiss key is scoped per-subscription-id, not per-user, so a fresh
 * cancellation cycle later gets its own banner state.
 *
 * Hidden on `/account` + `/account/*` — those routes get an in-page banner
 * variant instead so the messaging fits the surrounding UI density (same
 * pattern as `PastDueGlobalBanner`). The in-page variants (AccountSubscription
 * + AccountDashboard) share THIS banner's dismiss key
 * (`cancelled-banner-dismissed-<subId>`) so dismissing the banner on any one
 * surface dismisses it everywhere — the same cancellation shouldn't have to be
 * X-ed twice.
 *
 * NOTE on copy: the wording is intentionally generic ("subscription was
 * cancelled, subscribe again to restore"). When we eventually support
 * cancellation REASONS (refund, admin abuse-deny, retention exhaustion,
 * legal hold, etc.) the copy + CTA should branch on reason — tracked as
 * a Phase-8 follow-up in `docu/todo.md`.
 */
const CancelledGlobalBanner: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { myData } = useAuth();
  const userId = (myData as any)?.id ?? (myData as any)?._id ?? null;
  const [cancelledSubId, setCancelledSubId] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!userId) {
      setCancelledSubId(null);
      setDismissed(false);
      return;
    }
    instance.get("/subscriptions/my/history")
      .then(({ data }) => {
        const cancelled = findAnnouncableCancelledSub(data);
        if (cancelled) {
          setCancelledSubId(cancelled.id);
          setDismissed(
            localStorage.getItem(`cancelled-banner-dismissed-${cancelled.id}`) === "1",
          );
        } else {
          setCancelledSubId(null);
          setDismissed(false);
        }
      })
      .catch(() => { setCancelledSubId(null); setDismissed(false); });
  }, [userId, pathname]);

  const showBanner = !!cancelledSubId && !dismissed;

  // Publish rendered height so page layouts can offset top content.
  // useLayoutEffect (not useEffect) so the variable is set BEFORE the
  // browser paints — otherwise the first frame paints with --cancelled-
  // banner-h still at 0px and page content briefly overlaps the banner.
  useBannerOffset("--cancelled-banner-h", showBanner, bannerRef);

  if (!showBanner) return null;
  if (pathname === "/account" || pathname.startsWith("/account/")) return null;
  // Suppress on /account-deleted too — the grace-state landing page is its
  // own self-contained "account ended" surface; a subscription-cancelled
  // banner on top would be redundant and confusing.
  if (pathname === "/account-deleted") return null;

  const handleDismiss = () => {
    if (cancelledSubId) {
      localStorage.setItem(`cancelled-banner-dismissed-${cancelledSubId}`, "1");
    }
    // Trigger CSS fade-out first. Only flip `dismissed` (which unmounts
    // the banner) after the transition has finished — otherwise the
    // component would return null on the very next render and skip the
    // animation entirely.
    setFadingOut(true);
    window.setTimeout(() => {
      setDismissed(true);
      setFadingOut(false);
    }, 360);
  };

  const handleSubscribe = () => {
    // `?manage=1` is the existing AccountSubscription deep-link that
    // auto-opens the Manage Subscription modal on mount (see
    // useSearchParams handler in AccountSubscription/index.tsx). Skips
    // the extra "scroll + click Manage" step a re-subscribing user
    // would otherwise have to take.
    navigate("/account/subscription?manage=1");
  };

  return (
    <div
      data-banner-surface
      ref={bannerRef}
      data-global-banner
      style={{
        width: "100%",
        ...bannerSurface("#1f8c66"),
        padding: fadingOut ? "0 24px" : "12px 24px",
        marginTop: -1,
        maxHeight: fadingOut ? 0 : 200,
        opacity: fadingOut ? 0 : 1,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        color: "#fff",
        fontFamily: '"Raleway", sans-serif',
        fontSize: "0.875rem",
        position: "relative",
        transition: "opacity 360ms cubic-bezier(0.4, 0, 0.2, 1), max-height 360ms cubic-bezier(0.4, 0, 0.2, 1), padding 360ms cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <span style={{ fontWeight: 500 }}>
        Your subscription was cancelled — you're now on the Free plan. Subscribe again to restore full access.
      </span>
      <button
        onClick={handleSubscribe}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          padding: "6px 14px",
          borderRadius: 6,
          background: "linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%)",
          border: "none",
          color: "#2a1d14",
          fontFamily: '"Raleway", sans-serif',
          fontSize: "0.8125rem",
          fontWeight: 600,
          cursor: "pointer",
          whiteSpace: "nowrap",
          boxShadow: "0 0 12px rgba(186, 144, 92, 0.25)",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 16px rgba(186, 144, 92, 0.5)";
          e.currentTarget.style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "0 0 12px rgba(186, 144, 92, 0.25)";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        {/* Same redo glyph the account page's Reactivate button uses — this
            button does the same thing, so it should look like it. Drawn inline
            rather than through ButtonGlyph because this banner styles itself
            inline throughout. */}
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#60a3d7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M21 2v6h-6" />
          <path d="M21 13a9 9 0 1 1-3-7.7L21 8" />
        </svg>
        Re-Subscribe
      </button>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="banner-dismiss"
        style={{
          position: "absolute",
          top: "50%",
          right: 16,
          transform: "translateY(-50%)",
          width: 32,
          height: 32,
          background: "transparent",
          border: "none",
          color: "rgba(255,255,255,0.98)",
          cursor: "pointer",
          padding: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "color 0.15s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = "#fff"; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.98)"; }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};

export default CancelledGlobalBanner;
